import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, PopoverShell, TagGeneral, Text, TooltipTrigger } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { getEffectiveProgress } from '@/features/trainings/utils/llmJudgeProgress';
import ActiveProgressCard from './ActiveProgressCard';
import TrainingProgress from './TrainingProgress';

/** "optimizing" / "llm_as_a_judge" → "Optimizing" / "Llm as a judge". */
const statusLabel = (status) => {
    const label = String(status || '').split('_').join(' ').toLowerCase();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
};

/**
 * Docked training card (Figma KcQvkthzkvYFRqHHj0hCo4 3730:8169 / 3730:7924).
 * Progress and status stay live through the GlobalContext WebSocket train
 * statuses. The trash control opens a stop-training confirm.
 */
const TrainingProgressBanner = ({
    distillationEventId,
    progress,
    status,
    errorDetails,
    usecaseId,
    version,
    onConfirmStop,
}) => {
    const { trainStatuses, evaluationProgress } = useContext(GlobalContext);
    const liveEntry = trainStatuses[distillationEventId] || {};
    const liveStatus = liveEntry.status || status;
    const liveErrorDetails = liveEntry.error_details || errorDetails;
    const liveProgress = getEffectiveProgress(
        liveStatus,
        liveEntry.progress ?? progress,
        evaluationProgress,
        usecaseId || liveEntry.usecase_id,
        version,
    );

    const [confirmOpen, setConfirmOpen] = useState(false);
    const closeAreaRef = useRef(null);

    useEffect(() => {
        if (!confirmOpen) return undefined;
        const onMouseDown = (event) => {
            if (!closeAreaRef.current?.contains(event.target)) {
                setConfirmOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setConfirmOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [confirmOpen]);

    const tag = <TagGeneral label={statusLabel(liveStatus)} />;

    return (
        <ActiveProgressCard
            iconSlug="training-robot"
            title="Training in progress"
            tag={
                liveStatus === 'failed' && liveErrorDetails ? (
                    <TooltipTrigger
                        text={`Error Details: ${liveErrorDetails}`}
                        placement="top"
                    >
                        <span>{tag}</span>
                    </TooltipTrigger>
                ) : (
                    tag
                )
            }
            percent={liveProgress}
            deleteAriaLabel="Delete training"
            confirmOpen={confirmOpen}
            onDeleteClick={() => setConfirmOpen((open) => !open)}
            closeAreaRef={closeAreaRef}
            confirmDialog={(
                <div className="training-progress-banner__confirm" role="dialog" aria-label="Stop training">
                    <PopoverShell
                        tip
                        tipDirection="bottom"
                        tipAlign="end"
                        className="training-progress-banner__confirm-shell"
                        panelClassName="training-progress-banner__confirm-panel"
                    >
                        <div className="training-progress-banner__confirm-body">
                            <Text variant="base-semibold" as="p">
                                Stop training
                            </Text>
                            <Text variant="base-regular" color="secondary" as="p">
                                Are you sure you want to stop this training?
                            </Text>
                        </div>
                        <div className="training-progress-banner__confirm-actions">
                            <Button
                                variant="tertiary"
                                size="small"
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                danger
                                type="button"
                                disableWhenReadOnly
                                onClick={() => {
                                    setConfirmOpen(false);
                                    onConfirmStop?.();
                                }}
                            >
                                Stop
                            </Button>
                        </div>
                    </PopoverShell>
                </div>
            )}
        >
            <div className="training-progress-banner__progress">
                <TrainingProgress
                    distillationEventId={distillationEventId}
                    initialProgress={progress}
                    initialStatus={status}
                    usecaseId={usecaseId}
                    version={version}
                />
            </div>
        </ActiveProgressCard>
    );
};

export default TrainingProgressBanner;
